import React, { useRef, useState } from "react";
import { IClub, IDog, IDogRun } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";
import { Modal, useModal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import ManualRunEntryModal from "src/routes/DogProfile/ManualRunEntry/ManualRunEntryModal";
import MomentComposer from "../MomentComposer/MomentComposer";
import RunShareComposer from "../RunShareComposer/RunShareComposer";
import RunResultQuickAction, { ISportOption } from "../RunResultQuickAction/RunResultQuickAction";
import ClubPostComposer from "src/routes/KennelClub/components/ClubPostComposer/ClubPostComposer";

import styles from "./QuickActions.module.scss";

// "video" is intentionally not offered yet; MomentComposer already supports it
// for when we re-add the option.
type ActiveAction = "photo" | "run_result" | null;

// Exact icons from the design source (inline, not the app's mask-based Icon
// component — these are precise multi-path glyphs at a fixed 15x15 that
// don't exist in the shared icon set, and stroke="currentColor" makes them
// track the button's text color automatically).
const PhotoIcon: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="9" cy="10" r="2" />
        <path d="m3 17 5-5 4 4 3-3 6 6" />
    </svg>
);

const RunResultIcon: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M12 20v-6" />
        <path d="M6 20V10" />
        <path d="M18 20V4" />
    </svg>
);

interface IProps {
    onPosted: () => void;
    /** On a dog's profile: every composer is pinned to this dog. */
    dog?: IDog;
    /**
     * On a kennel club page (managers only): posts are authored by the club.
     * Photo + caption only; the run-result action is not offered.
     */
    club?: IClub;
}

const QuickActions: React.FC<IProps> = ({ onPosted, dog, club }) => {
    const user = helperService.getUser();
    const avatarUrl = club ? club.logo_url : user.image_url;
    const avatarName = club ? club.club_name : (user.display_name || "?");
    const placeholder = club
        ? `Post an update from ${club.club_name}…`
        : dog
            ? `Share a run or a photo of ${dog.call_name}…`
            : "Share a run, a title, a photo…";
    const [active, setActive] = useState<ActiveAction>(null);
    const [runEntryTarget, setRunEntryTarget] = useState<{ dog: IDog; sport: ISportOption } | null>(null);
    // Set once a run is saved; opens the "Share this run" step.
    const [shareTarget, setShareTarget] = useState<{ dog: IDog; run: Partial<IDogRun> } | null>(null);
    // ManualRunEntryModal calls onSuccess then onClose in the same tick, so
    // state set in onSuccess is not visible to onClose yet; a ref bridges them.
    const sharePending = useRef(false);
    const modal = useModal();

    const open = (action: ActiveAction) => {
        setActive(action);
        modal.toggle();
    };

    const close = () => {
        modal.toggle();
        setActive(null);
        setRunEntryTarget(null);
    };

    const handleRunSuccess = (createdRun?: any) => {
        const target = runEntryTarget;
        if (createdRun?._id && target) {
            sharePending.current = true;
            setShareTarget({ dog: target.dog, run: createdRun });
        } else {
            onPosted();
        }
    };

    const closeRunEntry = () => {
        setRunEntryTarget(null);
        if (sharePending.current) {
            // Tear down the run-entry flow but leave the queued share modal up.
            sharePending.current = false;
            setActive(null);
            if (modal.isShown) modal.toggle();
            return;
        }
        close();
    };

    const closeShare = () => setShareTarget(null);

    return (
        <div className={styles.QuickActions}>
            <div className={styles.composerRow} onClick={() => open("photo")}>
                <div className={styles.avatar}>
                    {avatarUrl
                        ? <img src={avatarUrl} alt={avatarName} />
                        : <span>{avatarName.charAt(0).toUpperCase()}</span>}
                </div>
                <div className={styles.composerPlaceholder}>{placeholder}</div>
            </div>

            <div className={styles.actionsRow}>
                <Button variant="secondary" className={styles.actionButton} onClick={() => open("photo")}>
                    <PhotoIcon />
                    Photo
                </Button>
                {!club && (
                    <Button variant="secondary" className={styles.actionButton} onClick={() => open("run_result")}>
                        <RunResultIcon />
                        Run result
                    </Button>
                )}
            </div>

            <Modal
                isShown={modal.isShown && active === "photo"}
                hide={close}
                className="momentComposerModal"
                modalContent={
                    active !== "photo" ? <></> : club ? (
                        <ClubPostComposer
                            club={club}
                            onClose={close}
                            onSuccess={onPosted}
                        />
                    ) : (
                        <MomentComposer
                            mediaType="image"
                            dog={dog}
                            onClose={close}
                            onSuccess={onPosted}
                        />
                    )
                }
            />

            {/* Run-result flow: owners only; never mounted in club mode. */}
            {!club && <>
            <Modal
                isShown={modal.isShown && active === "run_result" && !runEntryTarget}
                hide={close}
                className="runResultPickerModal"
                modalContent={
                    <RunResultQuickAction
                        dog={dog}
                        onClose={close}
                        onContinue={(pickedDog, sport) => setRunEntryTarget({ dog: pickedDog, sport })}
                    />
                }
            />

            {active === "run_result" && runEntryTarget && (
                <ManualRunEntryModal
                    isOpen={true}
                    onClose={closeRunEntry}
                    dog={runEntryTarget.dog}
                    sportType={runEntryTarget.sport.value}
                    sanctioningClub={runEntryTarget.sport.sanctioningClub}
                    onSuccess={handleRunSuccess}
                />
            )}

            <Modal
                isShown={!!shareTarget}
                hide={closeShare}
                className="momentComposerModal"
                modalContent={
                    shareTarget ? (
                        <RunShareComposer
                            dog={shareTarget.dog}
                            run={shareTarget.run}
                            onClose={closeShare}
                            onPosted={onPosted}
                        />
                    ) : <></>
                }
            />
            </>}
        </div>
    );
};

export default QuickActions;
